import { useEffect, useState, type FormEvent } from 'react';
import toast from 'react-hot-toast';
import {
  Shield, Plus, ShieldOff, Mail, Loader2,
  UserPlus, Send, Copy, Check, AlertTriangle, KeyRound,
} from 'lucide-react';
import {
  callListGlobalAdmins, callDemoteGlobalAdmin,
  callCreateGlobalAdminDirect, callPromoteGlobalAdmin,
  type GlobalAdmin, type CreateGlobalAdminResp,
} from '@/lib/functions';
import { useAuth } from '@/hooks/useAuth';
import { Loader } from '@/components/ui/loader';
import { Button, buttonClassName } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label, labelClassName } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { fmtDate } from '@/lib/utils';
import { useT } from '@/lib/i18n';

export default function AdminGlobals() {
  const { user } = useAuth();
  const { t } = useT();
  const [admins, setAdmins] = useState<GlobalAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await callListGlobalAdmins();
      setAdmins(data.admins);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const demote = async (a: GlobalAdmin) => {
    if (a.uid === user?.uid) return toast.error(t('admin.globals.cannot_self'));
    if (!confirm(t('admin.globals.demote_confirm', { email: a.email }))) return;
    setBusy(true);
    try {
      await callDemoteGlobalAdmin({ uid: a.uid });
      toast.success(t('admin.globals.demoted', { email: a.email }));
      refresh();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{t('admin.globals.title')}</h1>
          <p className="mt-1 text-sm text-ink-500">{t('admin.globals.subtitle')}</p>
        </div>
        <Button onClick={() => setShow(true)}>
          <Plus className="h-4 w-4" /> {t('admin.globals.add')}
        </Button>
      </header>

      {loading ? <Loader /> : admins.length === 0 ? (
        <Card className="text-sm text-ink-500">{t('admin.globals.empty')}</Card>
      ) : (
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>{t('admin.globals.col.user')}</TableHeadCell>
              <TableHeadCell>{t('admin.globals.col.email')}</TableHeadCell>
              <TableHeadCell>{t('admin.globals.col.created')}</TableHeadCell>
              <TableHeadCell>{t('admin.globals.col.last')}</TableHeadCell>
              <TableHeadCell className="text-right">{t('common.actions')}</TableHeadCell>
            </tr>
          </TableHead>
          <TableBody>
            {admins.map(a => {
              const isSelf = a.uid === user?.uid;
              return (
                <TableRow key={a.uid} className={busy ? 'opacity-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-amber-100 text-[0.7rem] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        <Shield className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <div className="font-medium text-ink-900 dark:text-white">{a.displayName ?? '—'}</div>
                        {isSelf && <div className="text-[0.65rem] text-brand-600 dark:text-brand-300">{t('common.you')}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-ink-700 dark:text-ink-200">
                    <Mail className="mr-1.5 inline h-3.5 w-3.5 text-ink-400" />{a.email}
                  </TableCell>
                  <TableCell className="text-ink-500">{a.createdAt ? fmtDate(new Date(a.createdAt)) : '—'}</TableCell>
                  <TableCell className="text-ink-500">{a.lastSignIn ? fmtDate(new Date(a.lastSignIn)) : t('common.never')}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => demote(a)}
                      disabled={busy || isSelf}
                      className={buttonClassName('ghost', 'text-rose-600 hover:bg-rose-50 disabled:opacity-30 dark:hover:bg-rose-900/30')}
                      title={isSelf ? t('admin.globals.cannot_self') : t('admin.globals.demote')}
                    >
                      <ShieldOff className="h-3.5 w-3.5" /> {t('admin.globals.demote')}
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
        <Shield className="mr-1 inline h-3.5 w-3.5" /> {t('admin.globals.security')}
      </div>

      {show && <AddGlobalAdminModal onClose={() => setShow(false)} onCreated={refresh} />}
    </div>
  );
}

// ─── Modal: create new account or promote existing ────────────────────────
function AddGlobalAdminModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useT();
  const [mode, setMode] = useState<'direct' | 'promote'>('direct');
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState<CreateGlobalAdminResp | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      if (mode === 'direct') {
        const { data } = await callCreateGlobalAdminDirect({
          email:       String(f.get('email')),
          displayName: String(f.get('displayName') ?? ''),
        });
        if (data.alreadyExisted) {
          toast.success(t('admin.globals.already', { email: data.email }));
          onCreated();
          onClose();
        } else {
          setCredentials(data);
          onCreated();
        }
      } else {
        const { data } = await callPromoteGlobalAdmin({ email: String(f.get('email')) });
        toast.success(t('admin.globals.promoted', { email: data.email }));
        onCreated();
        onClose();
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  if (credentials) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={t('admin.globals.created')}
        footer={<Button onClick={onClose}>{t('addu.created.ok')}</Button>}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Check className="h-4 w-4" />
          </div>
          <p className="text-sm text-ink-500">{t('admin.globals.created.desc')}</p>
        </div>
        <div className="space-y-3">
          <CopyField label={t('addu.created.email')} value={credentials.email} />
          {credentials.tempPassword && (
            <CopyField label={t('addu.created.pwd')} value={credentials.tempPassword} secret />
          )}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {t('addu.created.warn')}
          </div>
          {credentials.passwordResetLink && (
            <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-ink-700 dark:bg-ink-900/40">
              <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-200">{t('addu.created.alt')}</summary>
              <div className="mt-2"><CopyField label="" value={credentials.passwordResetLink} /></div>
            </details>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('admin.globals.modal.title')}
      description={t('admin.globals.modal.subtitle')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button type="submit" form="add-global-admin-form" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
             mode === 'direct' ? <UserPlus className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
            {mode === 'direct' ? t('admin.globals.create_cta') : t('admin.globals.promote_cta')}
          </Button>
        </>
      }
    >
      <form id="add-global-admin-form" onSubmit={submit} className="space-y-4">
        <div className="inline-flex w-full rounded-lg bg-slate-100 p-1 text-xs dark:bg-ink-900">
          <button type="button" onClick={() => setMode('direct')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium transition ${
              mode === 'direct'
                ? 'bg-white text-ink-900 shadow dark:bg-ink-700 dark:text-white'
                : 'text-ink-500 dark:text-ink-300'
            }`}>
            <UserPlus className="h-3.5 w-3.5" />
            <span>{t('addu.create_short')}</span>
            <Badge variant="success" className="ml-1 text-[0.6rem]">{t('common.recommended')}</Badge>
          </button>
          <button type="button" onClick={() => setMode('promote')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium transition ${
              mode === 'promote'
                ? 'bg-white text-ink-900 shadow dark:bg-ink-700 dark:text-white'
                : 'text-ink-500 dark:text-ink-300'
            }`}>
            <Send className="h-3.5 w-3.5" />
            <span>{t('addu.invite')}</span>
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-ink-600 dark:border-ink-700 dark:bg-ink-900/40 dark:text-ink-300">
          {mode === 'direct'
            ? <><KeyRound className="mr-1 inline h-3.5 w-3.5 text-brand-500" /> {t('admin.globals.modal.create_tip')}</>
            : <><Send className="mr-1 inline h-3.5 w-3.5 text-brand-500" /> {t('admin.globals.modal.promote_tip')}</>
          }
        </div>

        <div className="grid gap-4">
          {mode === 'direct' && (
            <div>
              <Label>{t('common.fullname')}</Label>
              <Input name="displayName" required className="mt-1" placeholder="Jean Tremblay" />
            </div>
          )}
          <div>
            <Label>{t('common.email')}</Label>
            <Input type="email" name="email" required className="mt-1" placeholder="prenom@cstools.ai" />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function CopyField({ label, value, secret, className }: { label: string; value: string; secret?: boolean; className?: string }) {
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(!secret);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={className}>
      {label && <div className={`${labelClassName()} mb-1`}>{label}</div>}
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-ink-700 dark:bg-ink-900/50">
        <code className="flex-1 truncate font-mono text-xs text-ink-900 dark:text-white">
          {reveal ? value : '•'.repeat(Math.min(20, value.length))}
        </code>
        {secret && (
          <button type="button" onClick={() => setReveal(r => !r)} className="text-xs text-ink-500 hover:text-brand-600">
            {reveal ? 'masquer' : 'afficher'}
          </button>
        )}
        <button type="button" onClick={copy} className="grid h-7 w-7 place-items-center rounded-md hover:bg-slate-200 dark:hover:bg-ink-700" title="Copier">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-ink-500" />}
        </button>
      </div>
    </div>
  );
}
